import { IScope, IRootScopeService } from "angular";
import { PlayDialogService } from "play-dialog/dist/scripts/PlayDialogService";
import { IApiLeagueRestriction, IApiTeam, IApiLeague } from "ng-league";
import { IGameAccountInfo } from "../typings/typings";

export class DialogService {
    public static $inject = ["PlayDialogService", "$translate", "$rootScope"];
    constructor(private PlayDialogService: PlayDialogService, private $translate, private $rootScope: IRootScopeService) {}

    public openGameAccountDialog(
        scope: any,
        gameAccount: IGameAccountInfo,
        isLoggedIn: Boolean,
        isValid: Boolean,
        leagueId?: number
    ): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);
        newScope.loggedIn = isLoggedIn;
        newScope.isValid = isValid;
        newScope.gameAccount = gameAccount;
        newScope.leagueId = leagueId;
        const title = gameAccount.description.name || this.$translate.instant("game-id-dialog:title");

        return this.PlayDialogService.openDialog({
            title: title,
            template: `
                <game-account-dialog
                    close="$close(value)"
                    editable="loggedIn"
                    valid="isValid"
                    league-id="leagueId"
                    game-account="gameAccount">
                </game-account-dialog>
            `,
            scope: newScope,
        });
    }

    public openTeamSelectDialog(scope: { leagueId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("team-select-dialog:title"),
            template: `
                <choose-team
                    [league-id]="leagueId"
                    (save)="$close($event)"
                    (cancel)="$close($event)"
                ></choose-team>
            `,
            scope: newScope,
        });
    }

    public openTeamManagementDialog(scope: { leagueId: number; teamId: number; isRosterLeague: boolean }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("team-management-dialog:title"),
            template: `
                <div class="u-dialog-inner">
                    <roster-status-dialog
                        [league-id]="leagueId"
                        [team-id]="teamId"
                        [is-roster-league]="isRosterLeague"
                        (close)="$close($event)"
                    ></roster-status-dialog>
                </div>
            `,
            scope: newScope,
        });
    }

    public openRosterStatusDialog(scope: { leagueId: number; teamId: number; isRosterLeague: boolean }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("roster-status-dialog:title"),
            template: `
                <div class="u-dialog-inner">
                    <roster-status-dialog
                        [league-id]="leagueId"
                        [team-id]="teamId"
                        [is-roster-league]="isRosterLeague"
                        (close)="$close($event)"
                    ></roster-status-dialog>
                </div>
            `,
            scope: newScope,
        });
    }

    public openRosterPreselectDialog(scope: { leagueId: number; teamId: number; isRosterLeague: boolean }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("manage-roster:title"),
            template: `
                <div class="u-dialog-inner">
                    <roster-preselection-dialog
                        [leagueId]="leagueId"
                        [teamId]="teamId"
                        [isRosterLeague]="isRosterLeague"
                        (cancel)="$close($event)"
                        (save)="$close($event)"
                    ></roster-preselection-dialog>
                </div>
            `,
            scope: newScope,
        });
    }

    public openRosterSelectDialog(scope: { leagueId: number; teamId: number; isRosterLeague: boolean }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("manage-roster:title"),
            template: `
                <div class="u-dialog-inner">
                    <roster-selection-dialog
                        [leagueId]="leagueId"
                        [teamId]="teamId"
                        [isRosterLeague]="isRosterLeague"
                        (cancel)="$close($event)"
                        (save)="$close($event)"
                    ></roster-selection-dialog>
                </div>
            `,
            scope: newScope,
        });
    }

    public openLeaveLeagueDialog(scope: { leagueId: number; contestantId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("leave-league-dialog:title"),
            template: `
                <leave-league
                    [league-id]="leagueId"
                    [contestant-id]="contestantId"
                    (cancel)="$close($event)"
                ></leave-league>
            `,
            scope: newScope,
            type: "destructive",
        });
    }

    public openLeaveRosterDialog(scope: { leagueId: number; teamId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("leave-roster-dialog:title"),
            template: `
                <leave-roster
                    [league-id]="leagueId"
                    [team-id]="teamId"
                    (cancel)="$close($event)"
                ></leave-roster>
            `,
            scope: newScope,
            type: "destructive",
        });
    }

    public openRestrictionDialog(scope: any, restriction: IApiLeagueRestriction, supported: boolean): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);
        newScope.restriction = restriction;
        newScope.supported = supported;

        let type = "restriction";
        if (supported) {
            type = restriction.type;
        }

        const obj = {
            title: supported ? this.$translate.instant(`sign-up-progress:${restriction.type}`) : restriction.type,
            scope: newScope,
        };

        // Fix for angularjs + angular AoT
        obj["templateUrl"] = `dialogs/restrictions/${type}.html`;

        return this.PlayDialogService.openDialog(obj);
    }

    public openSignupDialog(scope: { leagueId: number; contestantId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("signup-dialog:title"),
            template: `
                <signup-contestant
                    [league-id]="leagueId"
                    [contestant-id]="contestantId"
                    (cancel)='$close($event)'>
                </signup-contestant>
            `,
            scope: newScope,
        });
    }

    public openFFAGroupPicker(scope: {
        step: 0 | 1;
        currentGroup: { group: number; round: number };
        rounds: number[];
        groups: { [roundNumber: number]: number[] };
        contestantGroups: { group: number; round: number }[];
        currentRound: number;
    }): Promise<{ group: number; round: number }> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("ranking-ffa-picker:title"),
            template: `
                <ranking-ffa-picker
                    [step]="step"
                    [rounds]="rounds"
                    [groups]="groups"
                    [player-groups]="contestantGroups"
                    [current-round]="currentRound"
                    [selected-group]="currentGroup"
                    (picked)="$close($event)"
                    (cancel)="$close()"
                ></ranking-ffa-picker>
            `,
            scope: newScope,
        });
    }

    public openMatchMediaUploadDialog(scope: { matchId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("match-media-upload:title"),
            template: `
                <div class="u-dialog-inner u-dialog-big">
                    <match-media-upload
                        [match-id]="matchId"
                        (done)="$close($event)"
                    ></match-media-upload>
                </div>
            `,
            scope: newScope,
        });
    }

    // User Team Dialogs
    public openLeaveTeamDialog(scope: { teamId: number; teamName: string; memberId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("leave-team:title"),
            template: `
                <div class="u-dialog-big">
                    <user-leave-team
                    [team-id]="teamId"
                    [team-name]="teamName"
                    [member-id]="memberId"
                    (cancel)="$close($event)">
                    </user-leave-team>
                </div>
            `,
            scope: newScope,
            type: "destructive",
        });
    }

    public openDeleteTeamDialog(scope: { teamId: number; teamName: string; memberId: number }): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("delete-team:title"),
            template: `
                <div class="u-dialog-big">
                    <user-delete-team
                    [team-id]="teamId"
                    [team-name]="teamName"
                    [member-id]="memberId"
                    (cancel)="$close($event)">
                    </user-delete-team>
                </div>
            `,
            scope: newScope,
            type: "destructive",
        });
    }

    public openCreateTeamDialog(scope: {}): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("create-team:title"),
            template: `
                <div class="u-dialog-big">
                    <user-create-team
                        [member-id]="memberId"
                        (save)="$close($event)">
                    </user-create-team>
                </div>
            `,
            scope: newScope,
        });
    }

    public openJoinTeamDialog(scope: {}): Promise<any> {
        const newScope: any = Object.assign(this.$rootScope.$new(), scope);

        return this.PlayDialogService.openDialog({
            title: this.$translate.instant("join-team:title"),
            template: `
                <div class="u-dialog-big">
                    <user-join-team
                        [member-id]="memberId"
                        (save)="$close($event)">
                    </user-join-team>
                </div>
            `,
            scope: newScope,
        });
    }
}
